import { detectPhoneNumber, escapeHtml, isHtmlString } from '../utils';

interface LinkStringProps {
  href: string;
  text?: string;
  isEmail?: boolean;
  isPhone?: boolean;
}

const LINK_REGEX = /https?:\/\/[^\s<]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|\(?\+?\d[\d\s()./-]{5,18}\d/g;
const URL_PATTERN = /^https?:\/\/[^\s<]+$/;
const EMAIL_PATTERN = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/;
const TRAILING_PUNCTUATION = '.,;:!?';

const trimTrailingPunctuation = (value: string) => {
  let trimmed = value;

  while (trimmed.length) {
    const lastCharacter = trimmed.slice(-1);

    if (TRAILING_PUNCTUATION.includes(lastCharacter)) {
      trimmed = trimmed.slice(0, -1);
      continue;
    }

    if (lastCharacter === ')' && !trimmed.includes('(')) {
      trimmed = trimmed.slice(0, -1);
      continue;
    }

    break;
  }

  return { trimmed, trailing: value.slice(trimmed.length) };
};

const getLink = ({ href, text, isEmail, isPhone }: LinkStringProps) => {
  const getHref = () => {
    switch (true) {
      case isEmail:
        return `mailto:${href}`;
      case isPhone:
        return `tel:${href}`;
      default:
        return href;
    }
  };

  return `<a href="${getHref()}" target='_blank' rel='noreferrer'>${text ?? href}</a>`;
};

const mapper = (text: string) => {
  return text
    .split('\n')
    .map((line) => {
      return line.replace(LINK_REGEX, (match) => {
        if (URL_PATTERN.test(match)) {
          const { trimmed, trailing } = trimTrailingPunctuation(match);

          return `${getLink({ href: trimmed })}${trailing}`;
        }

        if (EMAIL_PATTERN.test(match)) {
          const { trimmed, trailing } = trimTrailingPunctuation(match);

          return `${getLink({ href: trimmed, isEmail: true })}${trailing}`;
        }

        const e164PhoneNumber = detectPhoneNumber(match);

        if (!e164PhoneNumber) {
          return match;
        }

        return getLink({ href: e164PhoneNumber, text: match, isPhone: true });
      });
    })
    .join('\n');
};

const SKIPPED_NODE_NAMES = ['CODE', 'PRE'];

const linkifyTextNodes = (node: Node) => {
  Array.from(node.childNodes).forEach((childNode) => {
    const { nodeType, nodeName, nodeValue } = childNode;

    if (nodeType === Node.TEXT_NODE) {
      if (!nodeValue) {
        return;
      }

      const replacementTemplate = document.createElement('template');

      replacementTemplate.innerHTML = mapper(escapeHtml(nodeValue));

      childNode.replaceWith(replacementTemplate.content);

      return;
    }

    if (nodeName === 'A') {
      const anchorElement = childNode as Element;

      anchorElement.setAttribute('target', '_blank');
      anchorElement.setAttribute('rel', 'noreferrer');

      return;
    }

    if (SKIPPED_NODE_NAMES.includes(nodeName)) {
      return;
    }

    linkifyTextNodes(childNode);
  });
};

const linkify = (content: string) => {
  if (!isHtmlString(content)) {
    return mapper(content);
  }

  const template = document.createElement('template');

  template.innerHTML = content;

  linkifyTextNodes(template.content);

  return template.innerHTML;
};

export const useLinkify = () => ({ linkify });
